import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Loader2, Upload, Copy, ExternalLink, Trash2, LogOut, Check, AlertTriangle,
  MapPin, CalendarDays, RefreshCw, Plane,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const LS_KEY = "vdv_admin_token";

const imgUrl = (u) => (!u ? "" : String(u).startsWith("/api/") ? `${BACKEND}${u}` : u);
const fmtDate = (d) => { try { return new Date(d).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return d || ""; } };

const FONT = { fontFamily: "'Manrope', system-ui, sans-serif" };

export default function AdminImport() {
  const [token, setToken] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [authErr, setAuthErr] = useState("");

  const [text, setText] = useState("");
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [copied, setCopied] = useState("");

  const client = useCallback(
    () => axios.create({ baseURL: API, headers: { "X-Admin-Token": token } }),
    [token]
  );

  const loadTrips = useCallback(async (tk) => {
    setLoadingTrips(true);
    try {
      const c = axios.create({ baseURL: API, headers: { "X-Admin-Token": tk } });
      const { data } = await c.get("/admin/trips");
      setTrips(data || []);
      setAuthed(true);
      setAuthErr("");
    } catch (e) {
      if (e?.response?.status === 401) {
        setAuthed(false);
        localStorage.removeItem(LS_KEY);
        setToken("");
        setAuthErr("Token de administrador incorrecto.");
      }
    } finally {
      setLoadingTrips(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadTrips(token);
  }, [token, loadTrips]);

  const doLogin = () => {
    const t = tokenInput.trim();
    if (!t) return;
    localStorage.setItem(LS_KEY, t);
    setToken(t);
  };

  const logout = () => {
    localStorage.removeItem(LS_KEY);
    setToken("");
    setAuthed(false);
    setTrips([]);
  };

  const doImport = async () => {
    if (!text.trim()) return;
    setImporting(true);
    setResults([]);
    try {
      const { data } = await client().post("/admin/import", { items: text });
      setResults(data.results || []);
      await loadTrips(token);
    } catch (e) {
      setResults([{ ok: false, input: "(solicitud)", error: e?.response?.data?.detail || "Error de red" }]);
    } finally {
      setImporting(false);
    }
  };

  const del = async (tk) => {
    if (!window.confirm(`¿Borrar el itinerario ${tk}? El enlace dejará de funcionar.`)) return;
    try {
      await client().delete(`/admin/trips/${tk}`);
      setTrips((t) => t.filter((x) => x.token !== tk));
    } catch (e) { /* noop */ }
  };

  const publicUrl = (tk) => `${window.location.origin}/trip/${tk}`;
  const copy = async (tk) => {
    try {
      await navigator.clipboard.writeText(publicUrl(tk));
      setCopied(tk);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* noop */ }
  };

  // ─────────────────── Login gate ───────────────────
  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6" style={{ backgroundColor: "#121B28", ...FONT }} data-testid="admin-login">
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700&display=swap');`}</style>
        <div className="w-full max-w-sm">
          <img src="/brand/vdv-logo-sand.webp" alt="Viaja de Verdad" className="mx-auto h-12 w-auto" />
          <h1 className="mt-8 text-center text-xl font-semibold text-white">Panel de itinerarios</h1>
          <p className="mt-2 text-center text-sm font-light text-white/60">Introduce el token de administrador para migrar y gestionar itinerarios.</p>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doLogin()}
            placeholder="Token de administrador"
            data-testid="admin-token-input"
            className="mt-6 w-full rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-sm text-white outline-none placeholder:text-white/30 focus:border-white/40"
          />
          {authErr && <p className="mt-3 flex items-center gap-2 text-sm text-red-300" data-testid="admin-auth-error"><AlertTriangle className="h-4 w-4" />{authErr}</p>}
          <button
            onClick={doLogin}
            data-testid="admin-login-btn"
            className="mt-4 w-full rounded-lg bg-white px-4 py-3 text-sm font-semibold text-[#121B28] transition-colors hover:bg-[#F4EBD7]"
          >
            Entrar
          </button>
        </div>
      </div>
    );
  }

  // ─────────────────── Panel ───────────────────
  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1A1A1A]" style={FONT} data-testid="admin-panel">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700&display=swap');`}</style>

      <header className="sticky top-0 z-30 border-b border-[#E4E4E7] bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3.5">
          <div className="flex items-center gap-3">
            <img src="/brand/vdv-logo-navy.webp" alt="Viaja de Verdad" className="h-7 w-auto" />
            <span className="text-sm font-semibold text-[#52525B]">Migración de itinerarios</span>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-1.5 text-sm font-medium text-[#52525B] hover:text-[#1A1A1A]">
            <LogOut className="h-4 w-4" /> Salir
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-10">
        {/* Importador */}
        <section className="rounded-xl border border-[#E4E4E7] bg-white p-6 md:p-8" data-testid="admin-import-card">
          <h2 className="text-lg font-semibold">Importar itinerarios de Sofi</h2>
          <p className="mt-1 text-sm font-light text-[#52525B]">
            Pega las URLs (o los tokens) de los itinerarios que quieres preservar, una por línea. Se descargan de
            Sofi (texto e imágenes) y se guardan aquí con el mismo token, de modo que su enlace <code className="rounded bg-[#F4EBD7] px-1">/trip/&lt;token&gt;</code> sigue funcionando.
          </p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={"https://sofi.viajadverdad.com/trip/ufUabh-inUNF\nufUabh-inUNF"}
            data-testid="admin-import-textarea"
            rows={5}
            className="mt-4 w-full rounded-lg border border-[#E4E4E7] bg-[#F9F8F6] px-4 py-3 font-mono text-sm outline-none focus:border-[#8a9771]"
          />
          <button
            onClick={doImport}
            disabled={importing || !text.trim()}
            data-testid="admin-import-btn"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#121B28] px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {importing ? "Importando…" : "Importar"}
          </button>

          {results.length > 0 && (
            <div className="mt-6 space-y-2" data-testid="admin-import-results">
              {results.map((r, i) => (
                <div key={i} className={`flex flex-wrap items-center gap-2 rounded-lg border px-4 py-2.5 text-sm ${r.ok ? "border-[#8a9771]/40 bg-[#8a9771]/10" : "border-red-300 bg-red-50"}`}>
                  {r.ok ? <Check className="h-4 w-4 text-[#5a6a4b]" /> : <AlertTriangle className="h-4 w-4 text-red-500" />}
                  {r.ok ? (
                    <>
                      <span className="font-medium">{r.title || r.token}</span>
                      <span className="text-[#52525B]">· {r.token}</span>
                      {r.images && <span className="text-[#52525B]">· imágenes {r.images.ok}/{r.images.total}</span>}
                      <a href={`/trip/${r.token}`} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 font-medium text-[#121B28] underline">
                        Abrir <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </>
                  ) : (
                    <span className="text-red-700">{r.input} — {r.error}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Listado */}
        <section className="mt-10" data-testid="admin-trips-section">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Itinerarios guardados ({trips.length})</h2>
            <button onClick={() => loadTrips(token)} className="flex items-center gap-1.5 text-sm font-medium text-[#52525B] hover:text-[#1A1A1A]">
              <RefreshCw className={`h-4 w-4 ${loadingTrips ? "animate-spin" : ""}`} /> Actualizar
            </button>
          </div>

          {trips.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#E4E4E7] bg-white py-16 text-center text-sm font-light text-[#A1A1AA]">
              Todavía no hay itinerarios. Importa el primero arriba.
            </div>
          ) : (
            <div className="space-y-3">
              {trips.map((t) => (
                <div key={t.token} className="flex items-center gap-4 rounded-xl border border-[#E4E4E7] bg-white p-3 md:p-4" data-testid={`admin-trip-${t.token}`}>
                  <div className="h-16 w-24 shrink-0 overflow-hidden rounded-lg bg-[#F4EBD7]">
                    {t.hero_image ? (
                      <img src={imgUrl(t.hero_image)} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[#c19a6b]"><Plane className="h-5 w-5" /></div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-base font-semibold">{t.title || t.itinerary_title || "(sin título)"}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs font-light text-[#52525B]">
                      {t.destination && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{t.destination}</span>}
                      {t.duration_days ? <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{t.duration_days} días</span> : null}
                      <span className="rounded bg-[#F4EBD7] px-1.5 py-0.5 font-mono text-[10px] text-[#8a5c1e]">{t.token}</span>
                      {t.imported_at && <span className="text-[#A1A1AA]">· {fmtDate(t.imported_at)}</span>}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <a href={`/trip/${t.token}`} target="_blank" rel="noreferrer" title="Abrir" data-testid={`admin-open-${t.token}`}
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E4E4E7] text-[#52525B] transition-colors hover:bg-[#F9F8F6]">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    <button onClick={() => copy(t.token)} title="Copiar URL" data-testid={`admin-copy-${t.token}`}
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E4E4E7] text-[#52525B] transition-colors hover:bg-[#F9F8F6]">
                      {copied === t.token ? <Check className="h-4 w-4 text-[#5a6a4b]" /> : <Copy className="h-4 w-4" />}
                    </button>
                    <button onClick={() => del(t.token)} title="Borrar" data-testid={`admin-delete-${t.token}`}
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E4E4E7] text-red-500 transition-colors hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
